<template>
  <v-layout column>
    <ClientActivityDialog
      :opened.sync="activityDialog.opened"
      :companyId="activityDialog.companyId"
      :companyName="activityDialog.companyName"
      :activities.sync="activityDialog.activities"
      @handleNotification="handleNotification"
    />

    <v-layout>
      <v-spacer />
      <v-tooltip bottom>
        <span class="primary--text" slot="activator">What is this?</span>
        <span
          >This is your notifications grouped by client. You can click each
          client to view their activities.</span
        >
      </v-tooltip>
    </v-layout>
    <v-layout>
      <v-flex :xs8="allowGroupChanges" class="px-2">
        <div class="text-xs-left">Search</div>

        <v-text-field
          class="default-input subheading"
          label="Enter to search"
          v-model="activitySearch"
          single-line
          solo
          hide-details
          append-icon="search"
        />
      </v-flex>
      <v-flex v-if="allowGroupChanges" xs4 class="px-2">
        <div class="text-xs-left">Firm group</div>
        <v-select
          v-model="selectedFirmGroup"
          :items="[
            {
              name: 'All',
              id: -1,
            },
            ...currentFirm.firm_groups,
          ]"
          item-text="name"
          item-value="id"
          hide-details
          solo
          class="default-input"
        />
      </v-flex>
    </v-layout>

    <v-layout align-center>
      <v-tabs v-model="activeTab" color="transparent">
        <v-tab class="text-capitalize"> All </v-tab>
        <v-tab class="text-capitalize"> New </v-tab>
      </v-tabs>
      <v-spacer />
      <v-layout shrink>
        <v-flex
          shrink
          v-for="label in allActivityTypes"
          :key="label.class"
          class="text-xs-left px-2"
        >
          <div class="activity-dot mr-2" :class="label.class" />
          <span class="text-xs-left">{{ label.text }}</span>
        </v-flex>
        <div class="arrow" data-popper-arrow></div>
      </v-layout>
    </v-layout>

    <v-data-table
      :headers="headers"
      :items="activitiesByClient"
      :search="activitySearch"
      :pagination.sync="pagination"
      :class="
        $vuetify.breakpoint.mdAndUp
          ? 'overview-data-table'
          : 'mobile-overview-data-table'
      "
      :loading="tableLoading"
    >
      <template v-slot:items="props">
        <tr
          class="activity-row"
          :class="props.item.newActivities.length > 0 ? 'new-activity-row' : ''"
          @click="
            openActivityDialog(
              props.item.companyId,
              props.item.companyName,
              props.item.activities,
            )
          "
        >
          <td class="text-xs-left ph-no-capture">
            {{
              isEmpty(props.item.companyName)
                ? 'Old Notifications'
                : props.item.companyName
            }}
          </td>

          <td class="text-xs-left ph-no-capture">
            <span
              >{{ props.item.lastActivity.client_name }}
              {{ props.item.lastActivity.text }}</span
            >
            <span
              class="primary--text"
              v-if="props.item.newActivities.length > 1"
            >
              <span v-if="props.item.newActivities.length === 2"
                >(+ {{ props.item.newActivities.length - 1 }} new
                activity)</span
              >
              <span v-else
                >(+ {{ props.item.newActivities.length - 1 }} new
                activities)</span
              >
            </span>
          </td>

          <td class="text-xs-left">
            <span>{{
              moment(props.item.lastActivityTimestamp).fromNow()
            }}</span>
          </td>

          <td class="text-xs-right d-flex align-center">
            <div class="activity-label-container d-flex justify-end">
              <div
                v-for="activityType in props.item.activityLabels"
                class="activity-dot"
                :class="activityType"
              />
            </div>

            <div class="tooltip" role="tooltip">
              <div
                v-for="label in props.item.activityLabels"
                class="d-flex align-center px-1 py-1"
              >
                <div class="activity-dot mr-2" :class="label" />
                <span class="text-xs-left">{{
                  label.replaceAll('-', ' ')
                }}</span>
              </div>

              <div class="arrow" data-popper-arrow></div>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import { createPopper } from '@popperjs/core';
import API from '@/api';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import get from 'lodash/get';
import ClientActivityDialog from '@/components/dialogs/ClientActivityDialog';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
import { mapGetters } from 'vuex';

export default {
  components: {
    ClientActivityDialog,
  },
  mixins: [FirmPreferenceMixins],
  props: {
    allActivities: {
      type: Array,
    },
    tableLoading: {
      type: Boolean,
    },
  },
  data() {
    return {
      selectedFirmGroup: -1,
      activeTab: 0,
      activitySearch: '',
      pagination: {
        sortBy: 'lastActivityTimestamp',
        descending: true,
        rowsPerPage: 25,
      },
      headers: [
        {
          text: 'Entity',
          align: 'left',
          value: 'companyName',
        },
        {
          text: 'Most recent activities (click to view all)',
          align: 'left',
          value: 'lastActivity.text',
        },
        {
          text: 'Time',
          align: 'left',
          value: 'lastActivityTimestamp',
        },
      ],
      activityDialog: {
        opened: false,
        companyName: null,
        activities: [],
      },
      allActivityTypes: [
        {
          text: 'submitted questionnaire',
          class: 'submitted-questionnaire',
        },
        {
          text: 'uploaded document',
          class: 'uploaded-document',
        },
        {
          text: 'changed answer',
          class: 'changed-answer',
        },
        {
          text: 'updated information',
          class: 'updated-information',
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
    }),
    activitiesByClient() {
      const activitiesByClient = map(
        groupBy(
          this.allActivities
            .filter(activity => activity.client_id)
            .sort((a, b) => (a.created_at > b.created_at ? -1 : 1)),
          'company_id',
        ),
        activities => {
          const newActivities = this.getNewActivities(activities);

          return {
            companyId: activities[0].company_id,
            companyName: activities[0].company_name,
            firm_group: get(activities[0], 'user.firm_group.id', null),
            activities: activities.map(activity => {
              return {
                ...activity,
                label: this.getActivityLabel(activity),
              };
            }),
            lastActivity: activities[0],
            lastActivityTimestamp: activities[0].created_at,
            newActivities: newActivities,
            activityLabels: this.getActivityLabels(activities),
          };
        },
      )
        .filter(activity => {
          if (this.activeTab === 1) {
            return activity.newActivities.length > 0;
          } else {
            return true;
          }
        })
        .filter(activity => {
          return (
            this.selectedFirmGroup === -1 ||
            (activity.firm_group &&
              this.selectedFirmGroup === activity.firm_group)
          );
        });

      return activitiesByClient;
    },
  },

  methods: {
    moment,
    isEmpty,
    handleNotification(activity, openInNewWindow) {
      this.$emit('handleNotification', activity, openInNewWindow);
    },

    getNewActivities(activities) {
      return activities.filter(activity => {
        return activity.dismissed === false;
      });
    },

    getActivityLabels(activities) {
      return uniq(
        activities.map(activity => {
          return this.getActivityLabel(activity);
        }),
      );
    },

    getActivityLabel(activity) {
      if (/submitted (.)*/.test(activity.text)) {
        return 'submitted-questionnaire';
      } else if (
        /completed (.)*\. There are no remaining items\./.test(activity.text)
      ) {
        return 'submitted-questionnaire';
      } else if (/uploaded (.)* after submission/.test(activity.text)) {
        return 'uploaded-document';
      } else if (/uploaded (.)* outside of questionnaire/.test(activity.text)) {
        return 'uploaded-document';
      } else if (/changed answer for/.test(activity.text)) {
        return 'changed-answer';
      } else if (/updated information for/.test(activity.text)) {
        return 'updated-information';
      } else {
        return 'other';
      }
    },

    openActivityDialog(companyId, companyName, activities) {
      this.activityDialog = {
        opened: true,
        companyId,
        companyName,
        activities,
      };
    },

    renderTooltips() {
      const activityLabelContainers = document.querySelectorAll(
        '.activity-label-container',
      );

      activityLabelContainers.forEach(container => {
        const tooltip = container.nextElementSibling;

        const popperInstance = createPopper(container, tooltip, {
          placement: 'right',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        });

        function show() {
          tooltip.setAttribute('data-show', '');

          // We need to tell Popper to update the tooltip position
          // after we show the tooltip, otherwise it will be incorrect
          popperInstance.update();
        }

        function hide() {
          tooltip.removeAttribute('data-show');
        }

        const showEvents = ['mouseenter', 'focus'];
        const hideEvents = ['mouseleave', 'blur'];

        showEvents.forEach(event => {
          container.addEventListener(event, show);
        });

        hideEvents.forEach(event => {
          container.addEventListener(event, hide);
        });
      });
    },
  },

  watch: {
    activitiesByClient: {
      handler() {
        setTimeout(() => {
          this.renderTooltips();
        });
      },
    },

    'activityDialog.opened': {
      async handler(value) {
        if (value === false) {
          this.$emit('refresh-table');
        }
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.tooltip {
  background: white;
  color: #000;
  box-shadow: 0px 2px 3px 0px #0003;
  font-weight: bold;
  padding: 4px 8px;
  font-size: 13px;
  border-radius: 4px;
  display: none;
}

.tooltip[data-show] {
  display: block;
}

.arrow,
.arrow::before {
  position: absolute;
  width: 8px;
  height: 8px;
  background: inherit;
}

.arrow {
  visibility: hidden;
}

.arrow::before {
  visibility: visible;
  content: '';
  transform: rotate(45deg);
}

.tooltip[data-popper-placement^='top'] > .arrow {
  bottom: -4px;
}

.tooltip[data-popper-placement^='bottom'] > .arrow {
  top: -4px;
}

.tooltip[data-popper-placement^='left'] > .arrow {
  right: 4px;
}

.tooltip[data-popper-placement^='right'] > .arrow {
  left: -12px;
}

.activity-label-container {
}
</style>

<style lang="postcss">
.activity-row {
  cursor: pointer;

  td {
  }

  &:hover {
    background: #e9e9e9 !important;
  }

  .chip {
    font-weight: normal;
    border-radius: 2px;
  }
}

.new-activity-row {
  background-color: #f3f3f3;

  td {
    font-weight: bold !important;
  }
}

.activity-dot {
  display: inline-block;
  max-width: 8px;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-left: 2px;
  margin-right: 2px;

  &.submitted-questionnaire {
    background-color: #43a047;
  }

  &.uploaded-document {
    background-color: #039be5;
  }

  &.changed-answer {
    background-color: #e53935;
  }

  &.updated-information {
    background-color: #8b4513;
  }
  /*
  &.sent-messages {
    background-color: #E53935;
  } */
}
</style>
